import type { NavigationEntry } from "@positive/ui";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type AnyRoute,
} from "@tanstack/react-router";

import type { Access, RouteAccess } from "./identity/access";
import { noAccess } from "./identity/access";
import type { HostMode } from "./identity/host";
import { RootLayout } from "./RootLayout";
import { RouteError, RoutePending } from "./RouteStatus";
import { kindPermission as faKindPermission } from "../modules/assets/kinds";
import { assetsModule, assetsPermissions as fa } from "../modules/assets/permissions";
import { cashPermissions as cash } from "../modules/cash/permissions";
import { catalogPermissions } from "../modules/catalog/permissions";
import type { PriceListSearch } from "../modules/catalog/pages/PriceListsPage";
import { deviceModule, devicePermissions } from "../modules/device/permissions";
import { glPermissions } from "../modules/gl/permissions";
import { hrModule, hrPermissions as hr } from "../modules/hr/permissions";
import { inventoryModule, invPermissions } from "../modules/inventory/permissions";
import { loyaltyModule, loyaltyPermissions as ly } from "../modules/loyalty/permissions";
import { kindPermission as purchKindPermission } from "../modules/purchasing/kinds";
import { purchasingModule, purchPermissions } from "../modules/purchasing/permissions";
import { financePermissions } from "../modules/masterdata/finance/permissions";
import { organizationPermissions } from "../modules/masterdata/organization/permissions";
import { msgModule, msgPermissions } from "../modules/messaging/permissions";
import type { ChartSearch } from "../modules/masterdata/finance/pages/ChartOfAccountsPage";
import type { SettingsSearch } from "../modules/platform/pages/SettingsGroupPage";
import type { DimensionSearch } from "../modules/masterdata/finance/pages/DimensionsPage";
import { platformPermissions } from "../modules/platform/permissions";
import { posModule, posPermissions } from "../modules/pos/permissions";
import { receivablesModule, salesModule, salesPermissions as sales } from "../modules/sales/permissions";
import { analyticsModule, analyticsPermissions as rptb } from "../modules/reports/analyticsPermissions";
import { reportsModule, reportsPermissions as rpt } from "../modules/reports/permissions";
import { taxModule, taxPermissions as tax } from "../modules/tax/permissions";
import { crmModule, crmPermissions } from "../modules/crm/permissions";
import { procModule, procPermissions } from "../modules/proc/permissions";
import { scmModule, scmPermissions } from "../modules/scm/permissions";
import { qualityModule, qualityPermissions } from "../modules/quality/permissions";
import { fleetModule, fleetPermissions } from "../modules/fleet/permissions";
import { jewelModule, jewelPermissions } from "../modules/jewel/permissions";
import { leaseModule, leasePermissions } from "../modules/lease/permissions";
import { parkingModule, parkingPermissions } from "../modules/parking/permissions";
import { opsModule, opsPermissions } from "../modules/ops/permissions";
import { workModule, workPermissions } from "../modules/work/permissions";

export interface RouterContext {
  /** Side navigation entries (ADR-0010/D-2 navigation registry), before permission filtering. */
  navigation: readonly NavigationEntry[];
  /** Effective access of the signed-in user (deny by default). */
  access: Access;
  mode: HostMode;
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** Who may open the route. Every route declares it; a route without it is denied (deny by default). */
    access?: RouteAccess;
    /** Idle lock style of the screen: POS screens lock with the cashier PIN (P4). Default: back office. */
    lockMode?: "pos";
  }
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  staticData: { access: { public: true } },
  beforeLoad: ({ context }) => {
    // The operator console has no tenant home page.
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirects by throwing
    if (context.mode.kind === "console") throw redirect({ to: "/operator/tenants" });
  },
  component: lazyRouteComponent(() => import("../modules/home/pages/HomePage"), "HomePage"),
});

const systemStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/status",
  staticData: { access: { public: true } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/SystemStatusPage"), "SystemStatusPage"),
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/me/profile",
  staticData: { access: { public: true } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/MyProfilePage"), "MyProfilePage"),
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/users",
  staticData: { access: { permission: platformPermissions.userView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/UsersPage"), "UsersPage"),
});

const userRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/users/$userId",
  staticData: { access: { permission: platformPermissions.userView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/UserObjectPage"), "UserObjectPage"),
});

const rolesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/roles",
  staticData: { access: { permission: platformPermissions.roleView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/RolesPage"), "RolesPage"),
});

const roleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/roles/$roleId",
  staticData: { access: { permission: platformPermissions.roleView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/RoleObjectPage"), "RoleObjectPage"),
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/audit",
  staticData: { access: { permission: platformPermissions.auditView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/AuditPage"), "AuditPage"),
});

const optionalId = (value: unknown) => (typeof value === "string" && value !== "" ? value : undefined);

const settingsGroupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/settings/$group",
  // The group's view permission (platform settings or messaging channels) is checked by the page and the API.
  staticData: { access: { public: true } },
  // `?level=company&scope=<id>` opens the group at one company's values (Companies and branches checklist).
  validateSearch: (search: Record<string, unknown>): SettingsSearch => {
    const scope = optionalId(search["scope"]);
    return search["level"] === "company" && scope ? { level: "company", scope } : {};
  },
  component: lazyRouteComponent(() => import("../modules/platform/pages/SettingsGroupPage"), "SettingsGroupPage"),
});

const deviceProfilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/device-profiles",
  staticData: { access: { permission: devicePermissions.profileView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/DeviceProfilesPage"), "DeviceProfilesPage"),
});

const deviceAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/device/agents",
  staticData: { access: { permission: devicePermissions.agentView, module: deviceModule } },
  component: lazyRouteComponent(() => import("../modules/device/pages/AgentsPage"), "AgentsPage"),
});

const devicePairingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/device/pairing",
  staticData: { access: { permission: devicePermissions.agentPair, module: deviceModule } },
  component: lazyRouteComponent(() => import("../modules/device/pages/PairingPage"), "PairingPage"),
});

const deviceJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/device/jobs",
  staticData: { access: { permission: devicePermissions.jobView, module: deviceModule } },
  component: lazyRouteComponent(() => import("../modules/device/pages/JobsPage"), "JobsPage"),
});

const deviceReleasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/device/releases",
  staticData: { access: { permission: devicePermissions.releaseView, module: deviceModule } },
  component: lazyRouteComponent(() => import("../modules/device/pages/ReleasesPage"), "ReleasesPage"),
});

const deviceScansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/device/scans",
  staticData: { access: { permission: devicePermissions.scanView, module: deviceModule } },
  component: lazyRouteComponent(() => import("../modules/device/pages/ScansPage"), "ScansPage"),
});

const posProfilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/profiles",
  staticData: { access: { permission: posPermissions.profileView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/ProfilesPage"), "ProfilesPage"),
});

const posTerminalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/terminals",
  staticData: { access: { permission: posPermissions.terminalView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/TerminalsPage"), "TerminalsPage"),
});

const posShiftsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/shifts",
  staticData: { access: { permission: posPermissions.shiftView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/ShiftsPage"), "ShiftsPage"),
});

const posIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/sync-issues",
  staticData: { access: { permission: posPermissions.syncIssueView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/SyncIssuesPage"), "SyncIssuesPage"),
});

const posDrawerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/drawer",
  staticData: { access: { permission: posPermissions.drawerView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/DrawerPage"), "DrawerPage"),
});

const posCashAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/cash-audit",
  staticData: { access: { permission: posPermissions.cashAuditView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/CashAuditPage"), "CashAuditPage"),
});

const posSalesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/sales",
  staticData: { access: { permission: posPermissions.salesAnalysisView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/SalesPage"), "SalesPage"),
});

const posBranchOperationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/branch-operations",
  staticData: { access: { permission: posPermissions.branchOperationView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/BranchOperationsPage"), "BranchOperationsPage"),
});

const posKitchenTimesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/kitchen-times",
  staticData: { access: { permission: posPermissions.salesAnalysisView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/KitchenTimesPage"), "KitchenTimesPage"),
});

const posCardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/card-reconciliation",
  staticData: { access: { permission: posPermissions.salesView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/CardReconciliationPage"), "CardReconciliationPage"),
});

const posOverridesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pos/supervisor-overrides",
  staticData: { access: { permission: posPermissions.overrideView, module: posModule } },
  component: lazyRouteComponent(() => import("../modules/pos/pages/OverridesPage"), "OverridesPage"),
});

const exportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/exports",
  staticData: { access: { permission: platformPermissions.tenantExport } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/ExportsPage"), "ExportsPage"),
});

const platformSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operator/platform-settings",
  staticData: { access: { operator: true } },
  component: lazyRouteComponent(() => import("../modules/operator/pages/PlatformSettingsPage"), "PlatformSettingsPage"),
});

const accountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/accounts",
  staticData: { access: { permission: financePermissions.accountView, module: "md" } },
  validateSearch: (search: Record<string, unknown>): ChartSearch => {
    const account = optionalId(search["account"]);
    const create = optionalId(search["create"]);
    return { ...(account ? { account } : {}), ...(create ? { create } : {}) };
  },
  component: lazyRouteComponent(
    () => import("../modules/masterdata/finance/pages/ChartOfAccountsPage"),
    "ChartOfAccountsPage",
  ),
});

const accountGroupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/account-groups",
  staticData: { access: { permission: financePermissions.accountGroupView, module: "md" } },
  component: lazyRouteComponent(
    () => import("../modules/masterdata/finance/pages/AccountGroupsPage"),
    "AccountGroupsPage",
  ),
});

const dimensionSearch = (search: Record<string, unknown>): DimensionSearch => {
  const node = optionalId(search["node"]);
  const create = optionalId(search["create"]);
  return { ...(node ? { node } : {}), ...(create ? { create } : {}) };
};

const costCentersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/cost-centers",
  staticData: { access: { permission: financePermissions.costCenterView, module: "md" } },
  validateSearch: dimensionSearch,
  component: lazyRouteComponent(() => import("../modules/masterdata/finance/pages/DimensionsPage"), "CostCentersPage"),
});

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/projects",
  staticData: { access: { permission: financePermissions.projectView, module: "md" } },
  validateSearch: dimensionSearch,
  component: lazyRouteComponent(() => import("../modules/masterdata/finance/pages/DimensionsPage"), "ProjectsPage"),
});

const currenciesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/currencies",
  staticData: { access: { permission: financePermissions.currencyView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/masterdata/finance/pages/CurrenciesPage"), "CurrenciesPage"),
});

const partiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/parties",
  staticData: { access: { permission: financePermissions.partyView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/masterdata/finance/pages/PartiesPage"), "PartiesPage"),
});

const partyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/parties/$partyId",
  staticData: { access: { permission: financePermissions.partyView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/masterdata/finance/pages/PartyObjectPage"), "PartyObjectPage"),
});

const partySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/party-settings",
  staticData: { access: { permission: financePermissions.partyView, module: "md" } },
  component: lazyRouteComponent(
    () => import("../modules/masterdata/finance/pages/PartySettingsPage"),
    "PartySettingsPage",
  ),
});

const companiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/companies",
  staticData: { access: { permission: organizationPermissions.companyView, module: "md" } },
  component: lazyRouteComponent(
    () => import("../modules/masterdata/organization/pages/CompaniesPage"),
    "CompaniesPage",
  ),
});

const companyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/companies/$companyId",
  staticData: { access: { permission: organizationPermissions.companyView, module: "md" } },
  component: lazyRouteComponent(
    () => import("../modules/masterdata/organization/pages/CompanyObjectPage"),
    "CompanyObjectPage",
  ),
});

const itemsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/items",
  staticData: { access: { permission: catalogPermissions.itemView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/ItemsPage"), "ItemsPage"),
});

const itemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/items/$itemId",
  staticData: { access: { permission: catalogPermissions.itemView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/ItemObjectPage"), "ItemObjectPage"),
});

const barcodeLabelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/barcode-labels",
  staticData: { access: { permission: catalogPermissions.barcodePrint, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/BarcodeLabelsPage"), "BarcodeLabelsPage"),
});

const itemQuickUpdateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/item-quick-update",
  staticData: { access: { permission: catalogPermissions.quickUpdateView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/QuickUpdatePage"), "QuickUpdatePage"),
});

const lookupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/lookups",
  staticData: { access: { permission: catalogPermissions.lookupView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/LookupsPage"), "LookupsPage"),
});

const itemCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/item-categories",
  staticData: { access: { permission: catalogPermissions.lookupView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/ItemCategoriesPage"), "ItemCategoriesPage"),
});

const unitGroupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/unit-groups",
  staticData: { access: { permission: catalogPermissions.unitGroupView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/UnitGroupsPage"), "UnitGroupsPage"),
});

const warehousesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/warehouses",
  staticData: { access: { permission: catalogPermissions.warehouseView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/WarehousesPage"), "WarehousesPage"),
});

const accountFamiliesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/account-families",
  staticData: { access: { permission: catalogPermissions.accountFamilyView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/AccountFamiliesPage"), "AccountFamiliesPage"),
});

const modifierSetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/modifier-sets",
  staticData: { access: { permission: catalogPermissions.itemView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/ModifierSetsPage"), "ModifierSetsPage"),
});

const priceListKindOf = (search: Record<string, unknown>): PriceListSearch => {
  const kind = search["kind"];
  return kind === "general" || kind === "customer" || kind === "groups" || kind === "tariffs" ? { kind } : {};
};

const priceListsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/price-lists",
  staticData: { access: { permission: catalogPermissions.priceListView, module: "md" } },
  validateSearch: priceListKindOf,
  component: lazyRouteComponent(() => import("../modules/catalog/pages/PriceListsPage"), "PriceListsPage"),
});

const serviceTariffsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/service-tariffs",
  staticData: { access: { permission: catalogPermissions.serviceTariffView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/PriceListsPage"), "ServiceTariffsPage"),
});

const priceListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/price-lists/$kind/$listId",
  staticData: { access: { permission: catalogPermissions.priceListView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/PriceListObjectPage"), "PriceListObjectPage"),
});

const serviceTariffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/md/service-tariffs/$listId",
  staticData: { access: { permission: catalogPermissions.serviceTariffView, module: "md" } },
  component: lazyRouteComponent(() => import("../modules/catalog/pages/PriceListObjectPage"), "PriceListObjectPage"),
});

const journalVouchersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/journal-vouchers",
  staticData: { access: { permission: glPermissions.voucherView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/JournalVouchersPage"), "JournalVouchersPage"),
});

const journalVoucherRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/journal-vouchers/$voucherId",
  staticData: { access: { permission: glPermissions.voucherView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/JournalVoucherPage"), "JournalVoucherPage"),
});

const journalEntriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/journal-entries",
  staticData: { access: { permission: glPermissions.entryView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/JournalEntriesPage"), "JournalEntriesPage"),
});

const journalEntryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/journal-entries/$entryId",
  staticData: { access: { permission: glPermissions.entryView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/JournalEntryPage"), "JournalEntryPage"),
});

const recurringJournalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/recurring-journals",
  staticData: { access: { permission: glPermissions.recurringView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/RecurringJournalsPage"), "RecurringJournalsPage"),
});

const recurringRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/recurring-journals/runs",
  staticData: { access: { permission: glPermissions.recurringView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/RecurringRunsPage"), "RecurringRunsPage"),
});

const recurringJournalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/recurring-journals/$journalId",
  staticData: { access: { permission: glPermissions.recurringView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/RecurringJournalPage"), "RecurringJournalPage"),
});

const fiscalYearsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/fiscal-years",
  staticData: { access: { permission: glPermissions.fiscalYearView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/FiscalYearsPage"), "FiscalYearsPage"),
});

const revaluationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/revaluation",
  staticData: { access: { permission: glPermissions.revaluationView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/RevaluationPage"), "RevaluationPage"),
});

const ledgerSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/settings",
  staticData: { access: { permission: glPermissions.settingsView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/LedgerSettingsPage"), "LedgerSettingsPage"),
});

const trialBalanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/reports/trial-balance",
  staticData: { access: { permission: glPermissions.trialBalanceView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/TrialBalancePage"), "TrialBalancePage"),
});

const statementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/reports/statement",
  staticData: { access: { permission: glPermissions.statementView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/StatementPage"), "StatementPage"),
});

const financialStatementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/reports/financial-statements",
  staticData: { access: { permission: glPermissions.financialStatementsView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/FinancialStatementsPage"), "FinancialStatementsPage"),
});

const costCentreReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/reports/cost-centres",
  staticData: { access: { permission: glPermissions.costCenterReportView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/DimensionReportPage"), "CostCentreReportPage"),
});

const projectReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/reports/projects",
  staticData: { access: { permission: glPermissions.projectReportView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/DimensionReportPage"), "ProjectReportPage"),
});

const deletedVouchersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gl/reports/deleted-vouchers",
  staticData: { access: { permission: glPermissions.deletedVouchersView, module: "gl" } },
  component: lazyRouteComponent(() => import("../modules/gl/pages/DeletedVouchersPage"), "DeletedVouchersPage"),
});

const invOpeningStockListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/opening-stock",
  staticData: { access: { permission: invPermissions.openingStockView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "OpeningStockList"),
});

const invOpeningStockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/opening-stock/$documentId",
  staticData: { access: { permission: invPermissions.openingStockView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "OpeningStockObject"),
});

const invGoodsIssueListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/goods-issues",
  staticData: { access: { permission: invPermissions.goodsIssueView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "GoodsIssueList"),
});

const invGoodsIssueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/goods-issues/$documentId",
  staticData: { access: { permission: invPermissions.goodsIssueView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "GoodsIssueObject"),
});

const invGoodsReceiptListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/goods-receipts",
  staticData: { access: { permission: invPermissions.goodsReceiptView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "GoodsReceiptList"),
});

const invGoodsReceiptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/goods-receipts/$documentId",
  staticData: { access: { permission: invPermissions.goodsReceiptView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "GoodsReceiptObject"),
});

const invAdjustmentListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/adjustments",
  staticData: { access: { permission: invPermissions.adjustmentView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "AdjustmentList"),
});

const invAdjustmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/adjustments/$documentId",
  staticData: { access: { permission: invPermissions.adjustmentView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "AdjustmentObject"),
});

const invTransferListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/transfers",
  staticData: { access: { permission: invPermissions.transferView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "TransferList"),
});

const invTransferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/transfers/$documentId",
  staticData: { access: { permission: invPermissions.transferView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "TransferObject"),
});

const invStockTypeChangeListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/stock-type-changes",
  staticData: { access: { permission: invPermissions.stockTypeChangeView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "StockTypeChangeList"),
});

const invStockTypeChangeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/stock-type-changes/$documentId",
  staticData: { access: { permission: invPermissions.stockTypeChangeView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "StockTypeChangeObject"),
});

const invRevaluationListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/revaluations",
  staticData: { access: { permission: invPermissions.revaluationView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "RevaluationList"),
});

const invRevaluationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/revaluations/$documentId",
  staticData: { access: { permission: invPermissions.revaluationView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "RevaluationObject"),
});

const invAwaitingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/transfers-awaiting",
  staticData: { access: { permission: invPermissions.acceptanceView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "AwaitingReceiptList"),
});

const invAwaitingObjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/transfers-awaiting/$documentId",
  staticData: { access: { permission: invPermissions.acceptanceView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/routePages"), "AwaitingReceiptObject"),
});

const invCountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/counts",
  staticData: { access: { permission: invPermissions.countView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/CountsPage"), "CountsPage"),
});

const invCountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/counts/$countId",
  staticData: { access: { permission: invPermissions.countView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/CountPage"), "CountPage"),
});

const invLandedCostsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/landed-costs",
  staticData: { access: { permission: invPermissions.landedCostView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/LandedCostsPage"), "LandedCostsPage"),
});

const invLandedCostRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/landed-costs/$landedCostId",
  staticData: { access: { permission: invPermissions.landedCostView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/LandedCostPage"), "LandedCostPage"),
});

const invReplenishmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/replenishment",
  staticData: { access: { permission: invPermissions.reorderView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReplenishmentPage"), "ReplenishmentPage"),
});

const invReorderRulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reorder-rules",
  staticData: { access: { permission: invPermissions.reorderView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReorderRulesPage"), "ReorderRulesPage"),
});

const invCostingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/costing",
  staticData: { access: { permission: invPermissions.costingView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/CostingPage"), "CostingPage"),
});

const invReasonsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reason-codes",
  staticData: { access: { permission: invPermissions.reasonView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReasonCodesPage"), "ReasonCodesPage"),
});

const invWarehouseAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/warehouse-access",
  staticData: { access: { permission: catalogPermissions.warehouseView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/WarehouseAccessPage"), "WarehouseAccessPage"),
});

const invAccountSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/account-settings",
  staticData: { access: { permission: invPermissions.accountSettingsView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/AccountSettingsPage"), "AccountSettingsPage"),
});

const invReportStockCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/stock-card",
  staticData: { access: { permission: invPermissions.stockCardView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const invReportStockBalanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/stock-balance",
  staticData: { access: { permission: invPermissions.stockBalanceView, module: inventoryModule } },
  // Opened from the item page's on-hand fact (UX7): the report runs for that item.
  validateSearch: (search: Record<string, unknown>): { itemId?: string } => {
    const itemId = optionalId(search["itemId"]);
    return itemId ? { itemId } : {};
  },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const invReportValuationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/valuation",
  staticData: { access: { permission: invPermissions.valuationView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const invReportMovementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/movements",
  staticData: { access: { permission: invPermissions.movementsView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const invReportBatchAgeingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/batch-ageing",
  staticData: { access: { permission: invPermissions.batchAgeingView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const invReportSerialTraceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/serial-trace",
  staticData: { access: { permission: invPermissions.serialTraceView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const invReportSlowMovingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/slow-moving",
  staticData: { access: { permission: invPermissions.slowMovingView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const invReportReconciliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inv/reports/reconciliation",
  staticData: { access: { permission: invPermissions.reconciliationView, module: inventoryModule } },
  component: lazyRouteComponent(() => import("../modules/inventory/pages/ReportPage"), "ReportPage"),
});

const purchRequisitionListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/requisitions",
  staticData: { access: { permission: purchKindPermission("requisitions", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "RequisitionList"),
});

const purchRequisitionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/requisitions/$documentId",
  staticData: { access: { permission: purchKindPermission("requisitions", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "RequisitionObject"),
});

const purchRfqListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/rfqs",
  staticData: { access: { permission: purchKindPermission("rfqs", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "RfqList"),
});

const purchRfqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/rfqs/$documentId",
  staticData: { access: { permission: purchKindPermission("rfqs", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "RfqObject"),
});

const purchQuotationListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/quotations",
  staticData: { access: { permission: purchKindPermission("quotations", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "QuotationList"),
});

const purchQuotationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/quotations/$documentId",
  staticData: { access: { permission: purchKindPermission("quotations", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "QuotationObject"),
});

const purchOrderListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/orders",
  staticData: { access: { permission: purchKindPermission("orders", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "OrderList"),
});

const purchOrderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/orders/$documentId",
  staticData: { access: { permission: purchKindPermission("orders", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "OrderObject"),
});

const purchContractListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/contracts",
  staticData: { access: { permission: purchKindPermission("contracts", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "ContractList"),
});

const purchContractRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/contracts/$documentId",
  staticData: { access: { permission: purchKindPermission("contracts", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "ContractObject"),
});

const purchGoodsReceiptListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/goods-receipts",
  staticData: { access: { permission: purchKindPermission("goods-receipts", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "GoodsReceiptList"),
});

const purchGoodsReceiptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/goods-receipts/$documentId",
  staticData: { access: { permission: purchKindPermission("goods-receipts", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "GoodsReceiptObject"),
});

const purchInvoiceListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/invoices",
  staticData: { access: { permission: purchKindPermission("invoices", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "InvoiceList"),
});

const purchInvoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/invoices/$documentId",
  staticData: { access: { permission: purchKindPermission("invoices", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "InvoiceObject"),
});

const purchReturnRequestListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/return-requests",
  staticData: { access: { permission: purchKindPermission("return-requests", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "ReturnRequestList"),
});

const purchReturnRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/return-requests/$documentId",
  staticData: { access: { permission: purchKindPermission("return-requests", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "ReturnRequestObject"),
});

const purchReturnListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/returns",
  staticData: { access: { permission: purchKindPermission("returns", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "ReturnList"),
});

const purchReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/returns/$documentId",
  staticData: { access: { permission: purchKindPermission("returns", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "ReturnObject"),
});

const purchDebitNoteListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/debit-notes",
  staticData: { access: { permission: purchKindPermission("debit-notes", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "DebitNoteList"),
});

const purchDebitNoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/debit-notes/$documentId",
  staticData: { access: { permission: purchKindPermission("debit-notes", "view"), module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/routePages"), "DebitNoteObject"),
});

const purchGrIrRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/gr-ir",
  staticData: { access: { permission: purchPermissions.grIrView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/GrIrPage"), "GrIrPage"),
});

const purchReturnReasonsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/return-reasons",
  staticData: { access: { permission: purchPermissions.returnReasonView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/ReturnReasonsPage"), "ReturnReasonsPage"),
});

const purchReportPayablesAgingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/reports/payables-aging",
  staticData: { access: { permission: purchPermissions.payablesAgingView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/ReportPage"), "ReportPage"),
});

const purchReportSupplierStatementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/reports/supplier-statement",
  staticData: { access: { permission: purchPermissions.statementView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/ReportPage"), "ReportPage"),
});

const purchReportOpenOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/reports/open-orders",
  staticData: { access: { permission: purchPermissions.openOrdersView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/ReportPage"), "ReportPage"),
});

const purchReportPurchaseAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/reports/purchase-analysis",
  staticData: { access: { permission: purchPermissions.purchaseAnalysisView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/ReportPage"), "ReportPage"),
});

const purchReportMatchExceptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/reports/match-exceptions",
  staticData: { access: { permission: purchPermissions.matchExceptionsView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/ReportPage"), "ReportPage"),
});

const purchReportPriceHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/purch/reports/price-history",
  staticData: { access: { permission: purchPermissions.purchaseAnalysisView, module: purchasingModule } },
  component: lazyRouteComponent(() => import("../modules/purchasing/pages/ReportPage"), "ReportPage"),
});

const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/approvals",
  staticData: { access: { permission: platformPermissions.approvalLogView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/ApprovalsPage"), "ApprovalsPage"),
});

const approvalTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/approval-templates",
  staticData: { access: { permission: platformPermissions.approvalTemplateView } },
  component: lazyRouteComponent(
    () => import("../modules/platform/pages/ApprovalTemplatesPage"),
    "ApprovalTemplatesPage",
  ),
});

const numberSeriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/number-series",
  staticData: { access: { permission: platformPermissions.numberSeriesView } },
  component: lazyRouteComponent(() => import("../modules/platform/pages/NumberSeriesPage"), "NumberSeriesPage"),
});

const tenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operator/tenants",
  staticData: { access: { operator: true } },
  component: lazyRouteComponent(() => import("../modules/operator/pages/TenantsPage"), "TenantsPage"),
});

const tenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operator/tenants/$tenantId",
  staticData: { access: { operator: true } },
  component: lazyRouteComponent(() => import("../modules/operator/pages/TenantObjectPage"), "TenantObjectPage"),
});

/** Cash & Banks (plan module `finance`): one route per screen; components live in `modules/cash/pages/CashRoutes`. */
const cashPages = {
  CashRoutes: () => import("../modules/cash/pages/CashRoutes"),
  ForecastPage: () => import("../modules/cash/pages/ForecastPage"),
  SettingsPages: () => import("../modules/cash/pages/SettingsPages"),
};
const cashRoute = (path: string, permission: string, component: string, file: keyof typeof cashPages = "CashRoutes") =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: `/cash/${path}`,
    staticData: { access: { permission, module: "finance" } },
    component: lazyRouteComponent(cashPages[file] as () => Promise<Record<string, () => React.JSX.Element>>, component),
  });

const cashScreens: readonly [path: string, permission: string, list: string, item: string, param: string][] = [
  ["receipt-vouchers", cash.receiptView, "ReceiptVouchersPage", "ReceiptVoucherPage", "documentId"],
  ["payment-vouchers", cash.paymentView, "PaymentVouchersPage", "PaymentVoucherPage", "documentId"],
  ["bank-deposits", cash.depositView, "BankDepositsPage", "BankDepositPage", "documentId"],
  ["cash-transfers", cash.transferView, "CashTransfersPage", "CashTransferPage", "documentId"],
  ["cash-counts", cash.cashCountView, "CashCountsPage", "CashCountPage", "documentId"],
  ["bank-reconciliations", cash.reconciliationView, "BankReconciliationsPage", "BankReconciliationPage", "documentId"],
  ["card-settlements", cash.cardSettlementView, "CardSettlementsPage", "CardSettlementPage", "documentId"],
  ["debit-notes", cash.debitNoteView, "DebitNotesPage", "DebitNotePage", "documentId"],
  ["credit-notes", cash.creditNoteView, "CreditNotesPage", "CreditNotePage", "documentId"],
  ["payment-requests", cash.paymentRequestView, "PaymentRequestsPage", "PaymentRequestPage", "documentId"],
  ["debit-note-requests", cash.debitNoteRequestView, "DebitNoteRequestsPage", "DebitNoteRequestPage", "documentId"],
  ["credit-note-requests", cash.creditNoteRequestView, "CreditNoteRequestsPage", "CreditNoteRequestPage", "documentId"],
  ["prepaid-expenses", cash.prepaidView, "PrepaidExpensesPage", "PrepaidExpensePage", "documentId"],
  ["letters-of-guarantee", cash.guaranteeView, "LettersOfGuaranteePage", "LetterOfGuaranteePage", "documentId"],
  ["letters-of-credit", cash.creditLetterView, "LettersOfCreditPage", "LetterOfCreditPage", "documentId"],
  ["petty-cash-expenses", cash.pettyCashView, "PettyCashExpensesPage", "PettyCashExpensePage", "documentId"],
  [
    "petty-cash-replenishments",
    cash.replenishmentView,
    "PettyCashReplenishmentsPage",
    "PettyCashReplenishmentPage",
    "documentId",
  ],
  ["cheques", cash.chequeView, "ChequesRegisterPage", "ChequeRegisterItemPage", "instrumentId"],
  [
    "promissory-notes",
    cash.promissoryView,
    "PromissoryNotesRegisterPage",
    "PromissoryNoteRegisterItemPage",
    "instrumentId",
  ],
  [
    "guarantee-cheques",
    cash.guaranteeChequeView,
    "GuaranteeChequesRegisterPage",
    "GuaranteeChequeRegisterItemPage",
    "instrumentId",
  ],
  ["setup/banks", cash.bankView, "BanksSetupPage", "BankSetupRecordPage", "recordId"],
  ["setup/cash-accounts", cash.cashAccountView, "CashAccountsSetupPage", "CashAccountSetupRecordPage", "recordId"],
  ["setup/cheque-books", cash.chequeBookView, "ChequeBooksSetupPage", "ChequeBookSetupRecordPage", "recordId"],
  ["setup/card-acquirers", cash.cardAcquirerView, "CardAcquirersSetupPage", "CardAcquirerSetupRecordPage", "recordId"],
  [
    "setup/petty-cash-funds",
    cash.pettyCashFundView,
    "PettyCashFundsSetupPage",
    "PettyCashFundSetupRecordPage",
    "recordId",
  ],
];

const cashRoutes = [
  // Static segments rank above `$id`, so `/cash/setup/posting-settings` and `.../settings` win over the object routes.
  cashRoute("cash-flow-forecast", cash.forecastView, "ForecastPage", "ForecastPage"),
  cashRoute("cash-flow-forecast/settings", cash.forecastView, "ForecastSettingsPage", "SettingsPages"),
  cashRoute("setup/posting-settings", cash.settingsView, "PostingSettingsPage", "SettingsPages"),
  ...cashScreens.flatMap(([path, permission, list, item, param]) => [
    cashRoute(path, permission, list),
    cashRoute(`${path}/$${param}`, permission, item),
  ]),
];

/** Sales (plan module `sales`) and the receivables reports (plan module `finance`); components in `modules/sales/pages/SalesRoutes`. */

const salesBillingDueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/billing-due",
  staticData: { access: { permission: sales.billingDueView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "BillingDuePage"),
});

const salesAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/reports/sales-analysis",
  staticData: { access: { permission: sales.salesAnalysisView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "SalesAnalysisReportPage"),
});

const salesOpenOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/reports/open-orders",
  staticData: { access: { permission: sales.openOrdersView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "OpenOrdersReportPage"),
});

const arAgingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ar/reports/aging",
  staticData: { access: { permission: sales.agingView, module: receivablesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "AgingReportPage"),
});

const arCreditExposureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ar/reports/credit-exposure",
  staticData: { access: { permission: sales.creditExposureView, module: receivablesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "CreditExposureReportPage"),
});

const salesQuotationListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/quotations",
  staticData: { access: { permission: sales.quotationView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "QuotationsPage"),
});

const salesQuotationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/quotations/$documentId",
  staticData: { access: { permission: sales.quotationView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "QuotationPage"),
});

const salesOrderListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/orders",
  staticData: { access: { permission: sales.orderView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "OrdersPage"),
});

const salesOrderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/orders/$documentId",
  staticData: { access: { permission: sales.orderView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "OrderPage"),
});

const salesPickListListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/pick-lists",
  staticData: { access: { permission: sales.pickListView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "PickListsPage"),
});

const salesPickListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/pick-lists/$documentId",
  staticData: { access: { permission: sales.pickListView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "PickListPage"),
});

const salesDeliveryListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/deliveries",
  staticData: { access: { permission: sales.deliveryView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "DeliveriesPage"),
});

const salesDeliveryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/deliveries/$documentId",
  staticData: { access: { permission: sales.deliveryView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "DeliveryPage"),
});

const salesInvoiceListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/invoices",
  staticData: { access: { permission: sales.invoiceView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "InvoicesPage"),
});

const salesInvoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/invoices/$documentId",
  staticData: { access: { permission: sales.invoiceView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "InvoicePage"),
});

const salesReturnListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/returns",
  staticData: { access: { permission: sales.returnView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "ReturnsPage"),
});

const salesReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/returns/$documentId",
  staticData: { access: { permission: sales.returnView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "ReturnPage"),
});

const salesReturnRequestListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/return-requests",
  staticData: { access: { permission: sales.returnRequestView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "ReturnRequestsPage"),
});

const salesReturnRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/return-requests/$documentId",
  staticData: { access: { permission: sales.returnRequestView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "ReturnRequestPage"),
});

const salesPriceCreditListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/price-credits",
  staticData: { access: { permission: sales.priceCreditView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "PriceCreditsPage"),
});

const salesPriceCreditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/price-credits/$documentId",
  staticData: { access: { permission: sales.priceCreditView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "PriceCreditPage"),
});

const salesSalespeopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/setup/salespeople",
  staticData: { access: { permission: sales.salespersonView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "SalespeoplePage"),
});

const salesReturnReasonsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/setup/return-reasons",
  staticData: { access: { permission: sales.returnReasonView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "ReturnReasonsPage"),
});

const salesCustomerProfilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/setup/customer-profiles",
  staticData: { access: { permission: sales.customerProfileView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "CustomerProfilesPage"),
});

const salesPromotionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/setup/promotions",
  staticData: { access: { permission: sales.promotionView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "PromotionsPage"),
});

/** A promotion on its object page (`new` = create; S11 target picker and Excel import). */
const salesPromotionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/setup/promotions/$promotionId",
  staticData: { access: { permission: sales.promotionView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "PromotionObjectPage"),
});

const salesAccountSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/setup/accounts",
  staticData: { access: { permission: sales.accountSettingsView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "SalesAccountSettingsPage"),
});

const salesDiscountLimitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/setup/discount-limits",
  staticData: { access: { permission: sales.settingsView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "DiscountLimitsPage"),
});

const salesAccrualsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/accruals",
  staticData: { access: { permission: sales.accrualView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "AccrualsPage"),
});

const salesCommissionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/commission",
  staticData: { access: { permission: sales.commissionView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "CommissionRunsPage"),
});

const salesTargetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales/targets",
  staticData: { access: { permission: sales.targetView, module: salesModule } },
  component: lazyRouteComponent(() => import("../modules/sales/pages/SalesRoutes"), "SalesTargetsPage"),
});

const salesRoutes = [
  salesSalespeopleRoute,
  salesReturnReasonsRoute,
  salesCustomerProfilesRoute,
  salesPromotionsRoute,
  salesPromotionRoute,
  salesAccountSettingsRoute,
  salesDiscountLimitsRoute,
  salesAccrualsRoute,
  salesCommissionRoute,
  salesTargetsRoute,
  salesBillingDueRoute,
  salesAnalysisRoute,
  salesOpenOrdersRoute,
  arAgingRoute,
  arCreditExposureRoute,
  salesQuotationListRoute,
  salesQuotationRoute,
  salesOrderListRoute,
  salesOrderRoute,
  salesPickListListRoute,
  salesPickListRoute,
  salesDeliveryListRoute,
  salesDeliveryRoute,
  salesInvoiceListRoute,
  salesInvoiceRoute,
  salesReturnListRoute,
  salesReturnRoute,
  salesReturnRequestListRoute,
  salesReturnRequestRoute,
  salesPriceCreditListRoute,
  salesPriceCreditRoute,
];

const taxReturnsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/returns",
  staticData: { access: { permission: tax.returnView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/ReturnsPage"), "ReturnsPage"),
});

const taxReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/returns/$returnId",
  staticData: { access: { permission: tax.returnView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/ReturnPage"), "ReturnPage"),
});

const taxEInvoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/einvoices",
  staticData: { access: { permission: tax.eInvoiceView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/EInvoicesPage"), "EInvoicesPage"),
});

const taxCodesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/codes",
  staticData: { access: { permission: tax.codeView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/TaxCodesPage"), "TaxCodesPage"),
});

const taxDeterminationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/determination",
  staticData: { access: { permission: tax.determinationView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/DeterminationPage"), "DeterminationPage"),
});

const taxSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/settings",
  staticData: { access: { permission: tax.settingsView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/TaxSettingsPage"), "TaxSettingsPage"),
});

const taxLedgerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/reports/tax-ledger",
  staticData: { access: { permission: tax.taxLedgerView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/TaxRoutes"), "TaxLedgerReport"),
});

const taxReconciliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/reports/reconciliation",
  staticData: { access: { permission: tax.reconciliationView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/TaxRoutes"), "TaxReconciliationReport"),
});

const taxWithholdingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tax/reports/withholding",
  staticData: { access: { permission: tax.withholdingReportView, module: taxModule } },
  component: lazyRouteComponent(() => import("../modules/tax/pages/TaxRoutes"), "WithholdingReport"),
});

/* HR & Payroll (plan module `hr`): one route per screen; the self-service area needs only `hr.self_service.use`. */
const hrEmployeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/employees",
  staticData: { access: { permission: hr.employeeView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/EmployeesPage"), "EmployeesPage"),
});

const hrEmployeeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/employees/$employeeId",
  staticData: { access: { permission: hr.employeeView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/EmployeePage"), "EmployeePage"),
});

const hrAttendanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/attendance",
  staticData: { access: { permission: hr.attendanceDayView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/AttendancePage"), "AttendancePage"),
});

const hrLeaveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/leave",
  staticData: { access: { permission: hr.leaveRequestView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/LeavePage"), "LeavePage"),
});

const hrPayrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/payroll",
  staticData: { access: { permission: hr.payrollRunView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/PayrollPage"), "PayrollPage"),
});

const hrLoansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/loans",
  staticData: { access: { permission: hr.loanView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/LoansPage"), "LoansPage"),
});

const hrWorkSchedulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/work-schedules",
  staticData: { access: { permission: hr.workScheduleView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/WorkSchedulesPage"), "WorkSchedulesPage"),
});

const hrProvisionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/provisions",
  staticData: { access: { permission: hr.eosProvisionView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/ProvisionsPage"), "ProvisionsPage"),
});

const hrRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/requests",
  staticData: { access: { permission: hr.hrRequestView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/RequestsPage"), "RequestsPage"),
});

const hrSelfServiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/self",
  staticData: { access: { permission: hr.selfServiceUse, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/SelfServicePage"), "SelfServicePage"),
});

const hrSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hr/settings",
  staticData: { access: { permission: hr.settingsView, module: hrModule } },
  component: lazyRouteComponent(() => import("../modules/hr/pages/SettingsPage"), "SettingsPage"),
});

const hrRoutes = [
  hrEmployeesRoute,
  hrEmployeeRoute,
  hrAttendanceRoute,
  hrLeaveRoute,
  hrPayrollRoute,
  hrLoansRoute,
  hrWorkSchedulesRoute,
  hrProvisionsRoute,
  hrRequestsRoute,
  hrSelfServiceRoute,
  hrSettingsRoute,
];

const faRegisterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/register",
  staticData: { access: { permission: fa.assetView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/AssetsPage"), "AssetsPage"),
});

const faAssetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/register/$assetId",
  staticData: { access: { permission: fa.assetView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "AssetObject"),
});

const faClassesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/classes",
  staticData: { access: { permission: fa.classView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/ClassesPage"), "ClassesPage"),
});

const faAcquisitionListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/acquisitions",
  staticData: { access: { permission: faKindPermission("acquisitions", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "AcquisitionList"),
});

const faAcquisitionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/acquisitions/$documentId",
  staticData: { access: { permission: faKindPermission("acquisitions", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "AcquisitionObject"),
});

const faSettlementListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/settlements",
  staticData: { access: { permission: faKindPermission("settlements", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "SettlementList"),
});

const faSettlementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/settlements/$documentId",
  staticData: { access: { permission: faKindPermission("settlements", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "SettlementObject"),
});

const faTransferListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/transfers",
  staticData: { access: { permission: faKindPermission("transfers", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "TransferList"),
});

const faTransferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/transfers/$documentId",
  staticData: { access: { permission: faKindPermission("transfers", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "TransferObject"),
});

const faSaleListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/sales",
  staticData: { access: { permission: faKindPermission("sales", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "SaleList"),
});

const faSaleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/sales/$documentId",
  staticData: { access: { permission: faKindPermission("sales", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "SaleObject"),
});

const faScrapListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/scrappings",
  staticData: { access: { permission: faKindPermission("scrappings", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "ScrapList"),
});

const faScrapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/scrappings/$documentId",
  staticData: { access: { permission: faKindPermission("scrappings", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "ScrapObject"),
});

const faImpairmentListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/impairments",
  staticData: { access: { permission: faKindPermission("impairments", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "ImpairmentList"),
});

const faImpairmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/impairments/$documentId",
  staticData: { access: { permission: faKindPermission("impairments", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "ImpairmentObject"),
});

const faRevaluationListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/revaluations",
  staticData: { access: { permission: faKindPermission("revaluations", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "RevaluationList"),
});

const faRevaluationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/revaluations/$documentId",
  staticData: { access: { permission: faKindPermission("revaluations", "view"), module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "RevaluationObject"),
});

const faDepreciationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/depreciation",
  staticData: { access: { permission: fa.depreciationView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/RunsPage"), "RunsPage"),
});

const faLeasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/leases",
  staticData: { access: { permission: fa.leaseView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/LeasesPage"), "LeasesPage"),
});

const faLeaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/leases/$leaseId",
  staticData: { access: { permission: fa.leaseView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "LeaseObject"),
});

const faMaintenanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/maintenance",
  staticData: { access: { permission: fa.maintenanceView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/MaintenancePage"), "MaintenancePage"),
});

const faCountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/counts",
  staticData: { access: { permission: fa.countView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/CountsPage"), "CountsPage"),
});

const faCountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/counts/$countId",
  staticData: { access: { permission: fa.countView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "CountObject"),
});

const faReportRegisterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/reports/register",
  staticData: { access: { permission: fa.registerView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "RegisterReport"),
});

const faReportRollForwardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/reports/roll-forward",
  staticData: { access: { permission: fa.rollForwardView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "RollForwardReport"),
});

const faReportForecastRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/reports/depreciation-forecast",
  staticData: { access: { permission: fa.forecastView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "ForecastReport"),
});

const faReportLeaseMaturityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets/reports/lease-maturity",
  staticData: { access: { permission: fa.leaseMaturityView, module: assetsModule } },
  component: lazyRouteComponent(() => import("../modules/assets/pages/routePages"), "LeaseMaturityReport"),
});

const msgMonitorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/msg/monitor",
  staticData: { access: { permission: msgPermissions.outgoingView, module: msgModule } },
  component: lazyRouteComponent(() => import("../modules/messaging/pages/MonitorPage"), "MonitorPage"),
});

const msgSegmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/msg/segments",
  staticData: { access: { permission: msgPermissions.segmentView, module: msgModule } },
  component: lazyRouteComponent(() => import("../modules/messaging/pages/SegmentsPage"), "SegmentsPage"),
});

const msgConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/msg/consent",
  staticData: { access: { permission: msgPermissions.consentView, module: msgModule } },
  component: lazyRouteComponent(() => import("../modules/messaging/pages/ConsentPage"), "ConsentPage"),
});

const msgSurveysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/msg/surveys",
  staticData: { access: { permission: msgPermissions.surveyView, module: msgModule } },
  component: lazyRouteComponent(() => import("../modules/messaging/pages/SurveysPage"), "SurveysPage"),
});

const msgTriggersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/msg/triggers",
  staticData: { access: { permission: msgPermissions.triggerView, module: msgModule } },
  component: lazyRouteComponent(() => import("../modules/messaging/pages/TriggersPage"), "TriggersPage"),
});

const msgTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/msg/templates",
  staticData: { access: { permission: msgPermissions.templateView, module: msgModule } },
  component: lazyRouteComponent(() => import("../modules/messaging/pages/TemplatesPage"), "TemplatesPage"),
});

const msgAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/msg/accounts",
  staticData: { access: { permission: msgPermissions.accountView, module: msgModule } },
  component: lazyRouteComponent(() => import("../modules/messaging/pages/AccountsPage"), "AccountsPage"),
});

const workTasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/tasks",
  staticData: { access: { permission: workPermissions.taskView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/TasksPage"), "TasksPage"),
});

const workTaskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/tasks/$taskId",
  staticData: { access: { permission: workPermissions.taskView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/TaskPage"), "TaskPage"),
});

const workBoardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/board",
  staticData: { access: { permission: workPermissions.taskView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/BoardPage"), "BoardPage"),
});

const workCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/calendar",
  staticData: { access: { permission: workPermissions.taskView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/CalendarPage"), "CalendarPage"),
});

const workQueuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/queues",
  staticData: { access: { permission: workPermissions.taskView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/QueuesPage"), "QueuesPage"),
});

const workFormsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/forms",
  staticData: { access: { permission: workPermissions.formView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/FormsPage"), "FormsPage"),
});

const workFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/forms/$formId",
  staticData: { access: { permission: workPermissions.formView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/FormDesignerPage"), "FormDesignerPage"),
});

const workChecklistsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/checklists",
  staticData: { access: { permission: workPermissions.checklistView, module: workModule } },
  component: lazyRouteComponent(() => import("../modules/work/pages/ChecklistTemplatesPage"), "ChecklistTemplatesPage"),
});

const leaseLeasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lease/leases",
  staticData: { access: { permission: leasePermissions.contractView, module: leaseModule } },
  component: lazyRouteComponent(() => import("../modules/lease/pages/LeasesPage"), "LeasesPage"),
});

const leaseLeaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lease/leases/$leaseId",
  staticData: { access: { permission: leasePermissions.contractView, module: leaseModule } },
  component: lazyRouteComponent(() => import("../modules/lease/pages/LeasePage"), "LeasePage"),
});

const leasePropertiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lease/properties",
  staticData: { access: { permission: leasePermissions.propertyView, module: leaseModule } },
  component: lazyRouteComponent(() => import("../modules/lease/pages/PropertiesPage"), "PropertiesPage"),
});

const leaseUnitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lease/units",
  staticData: { access: { permission: leasePermissions.unitView, module: leaseModule } },
  component: lazyRouteComponent(() => import("../modules/lease/pages/UnitsPage"), "UnitsPage"),
});

const leaseStraightLineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lease/straight-line",
  staticData: { access: { permission: leasePermissions.straightLineView, module: leaseModule } },
  component: lazyRouteComponent(() => import("../modules/lease/pages/StraightLinePage"), "StraightLinePage"),
});

const leaseReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lease/reports",
  staticData: { access: { permission: leasePermissions.reportView, module: leaseModule } },
  component: lazyRouteComponent(() => import("../modules/lease/pages/ReportsPage"), "ReportsPage"),
});

const jewelSalesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/sales",
  staticData: { access: { permission: jewelPermissions.saleView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/SalesPage"), "SalesPage"),
});

const jewelSaleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/sales/$saleId",
  staticData: { access: { permission: jewelPermissions.saleView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/SalePage"), "SalePage"),
});

const jewelBuyBacksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/buybacks",
  staticData: { access: { permission: jewelPermissions.buyBackView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/BuyBacksPage"), "BuyBacksPage"),
});

const jewelTagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/tags",
  staticData: { access: { permission: jewelPermissions.tagView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/TagsPage"), "TagsPage"),
});

const jewelAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/gold-accounts",
  staticData: { access: { permission: jewelPermissions.accountView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/AccountsPage"), "AccountsPage"),
});

const jewelAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/gold-accounts/$accountId",
  staticData: { access: { permission: jewelPermissions.accountView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/AccountPage"), "AccountPage"),
});

const jewelRefiningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/refining",
  staticData: { access: { permission: jewelPermissions.refiningView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/RefiningPage"), "RefiningPage"),
});

const jewelWagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/wages",
  staticData: { access: { permission: jewelPermissions.wagesView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/WagesPage"), "WagesPage"),
});

const jewelReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/reports",
  staticData: { access: { permission: jewelPermissions.reportView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/ReportsPage"), "ReportsPage"),
});

const jewelPricesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/prices",
  staticData: { access: { permission: jewelPermissions.priceView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/PricesPage"), "PricesPage"),
});

const jewelItemsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jewel/items",
  staticData: { access: { permission: jewelPermissions.itemView, module: jewelModule } },
  component: lazyRouteComponent(() => import("../modules/jewel/pages/ItemsPage"), "ItemsPage"),
});

const parkingEntryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/entry",
  staticData: { access: { permission: parkingPermissions.entryCreate, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/EntryPage"), "EntryPage"),
});

const parkingExitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/exit",
  staticData: { access: { permission: parkingPermissions.exitView, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/ExitPage"), "ExitPage"),
});

const parkingTicketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/tickets",
  staticData: { access: { permission: parkingPermissions.entryView, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/TicketsPage"), "TicketsPage"),
});

const parkingShiftsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/shifts",
  staticData: { access: { permission: parkingPermissions.shiftView, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/ShiftsPage"), "ShiftsPage"),
});

const parkingTariffsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/tariffs",
  staticData: { access: { permission: parkingPermissions.tariffView, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/TariffsPage"), "TariffsPage"),
});

const parkingLanesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/lanes",
  staticData: { access: { permission: parkingPermissions.moduleView, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/LanesPage"), "LanesPage"),
});

const parkingCampaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/campaigns",
  staticData: { access: { permission: parkingPermissions.campaignView, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/CampaignsPage"), "CampaignsPage"),
});

const parkingReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parking/reports",
  staticData: { access: { permission: parkingPermissions.reportView, module: parkingModule } },
  component: lazyRouteComponent(() => import("../modules/parking/pages/ReportsPage"), "ReportsPage"),
});

const fleetVehiclesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/vehicles",
  staticData: { access: { permission: fleetPermissions.vehicleView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/VehiclesPage"), "VehiclesPage"),
});

const fleetVehicleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/vehicles/$vehicleId",
  staticData: { access: { permission: fleetPermissions.vehicleView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/VehiclePage"), "VehiclePage"),
});

const fleetDriversRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/drivers",
  staticData: { access: { permission: fleetPermissions.driverView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/DriversPage"), "DriversPage"),
});

const fleetFuelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/fuel",
  staticData: { access: { permission: fleetPermissions.fuelView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/FuelPage"), "FuelPage"),
});

const fleetMaintenanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/maintenance",
  staticData: { access: { permission: fleetPermissions.maintenanceView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/MaintenancePage"), "MaintenancePage"),
});

const fleetAlertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/alerts",
  staticData: { access: { permission: fleetPermissions.vehicleView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/AlertsPage"), "AlertsPage"),
});

const fleetReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/reports",
  staticData: { access: { permission: fleetPermissions.reportView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/ReportsPage"), "ReportsPage"),
});

const fleetCarCardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/car-cards",
  staticData: { access: { permission: fleetPermissions.carCardView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/CarCardsPage"), "CarCardsPage"),
});

const fleetGarageOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/garage-orders",
  staticData: { access: { permission: fleetPermissions.garageOrderView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/GarageOrdersPage"), "GarageOrdersPage"),
});

const fleetClearancesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/clearances",
  staticData: { access: { permission: fleetPermissions.clearanceView, module: fleetModule } },
  component: lazyRouteComponent(() => import("../modules/fleet/pages/ClearancesPage"), "ClearancesPage"),
});

const procRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/requests",
  staticData: { access: { permission: procPermissions.requestView, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/RequestsPage"), "RequestsPage"),
});

const procRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/requests/$requestId",
  staticData: { access: { permission: procPermissions.requestView, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/RequestPage"), "RequestPage"),
});

const procSourcingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/sourcing",
  staticData: { access: { permission: procPermissions.requestConvert, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/SourcingPage"), "SourcingPage"),
});

const procTendersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/tenders",
  staticData: { access: { permission: procPermissions.tenderView, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/TendersPage"), "TendersPage"),
});

const procTenderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/tenders/$tenderId",
  staticData: { access: { permission: procPermissions.tenderView, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/TenderPage"), "TenderPage"),
});

const procRefillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/refills",
  staticData: { access: { permission: procPermissions.refillView, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/RefillsPage"), "RefillsPage"),
});

const procRefillRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/refills/$refillId",
  staticData: { access: { permission: procPermissions.refillView, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/RefillPage"), "RefillPage"),
});

const procSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/proc/setup",
  staticData: { access: { permission: procPermissions.serviceView, module: procModule } },
  component: lazyRouteComponent(() => import("../modules/proc/pages/SetupPage"), "SetupPage"),
});

const scmInternationalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/international",
  staticData: { access: { permission: scmPermissions.shipmentView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/ShipmentsPage"), "InternationalShipmentsPage"),
});

const scmInternationalShipmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/international/$shipmentId",
  staticData: { access: { permission: scmPermissions.shipmentView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/ShipmentPage"), "InternationalShipmentPage"),
});

const scmLocalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/local",
  staticData: { access: { permission: scmPermissions.localView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/ShipmentsPage"), "LocalShipmentsPage"),
});

const scmLocalShipmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/local/$shipmentId",
  staticData: { access: { permission: scmPermissions.localView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/ShipmentPage"), "LocalShipmentPage"),
});

const scmForecastsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/forecasts",
  staticData: { access: { permission: scmPermissions.forecastView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/ForecastsPage"), "ForecastsPage"),
});

const scmForecastRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/forecasts/$forecastId",
  staticData: { access: { permission: scmPermissions.forecastView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/ForecastPage"), "ForecastPage"),
});

const scmPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/stock-plans",
  staticData: { access: { permission: scmPermissions.planView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/StockPlansPage"), "StockPlansPage"),
});

const scmPlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/stock-plans/$planId",
  staticData: { access: { permission: scmPermissions.planView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/StockPlanPage"), "StockPlanPage"),
});

const scmAirwayBillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/airway-bills",
  staticData: { access: { permission: scmPermissions.airwayView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/AirwayBillsPage"), "AirwayBillsPage"),
});

const scmLoadingListsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/loading-lists",
  staticData: { access: { permission: scmPermissions.loadingView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/LoadingListsPage"), "LoadingListsPage"),
});

const scmLoadingListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/loading-lists/$loadingId",
  staticData: { access: { permission: scmPermissions.loadingView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/LoadingListPage"), "LoadingListPage"),
});

const scmLogisticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/document-logistics",
  staticData: { access: { permission: scmPermissions.logisticsView, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/LogisticsPage"), "LogisticsPage"),
});

const scmReferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scm/references",
  staticData: { access: { permission: scmPermissions.referenceImport, module: scmModule } },
  component: lazyRouteComponent(() => import("../modules/scm/pages/ReferencesPage"), "ReferencesPage"),
});

const qualityLotsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/lots",
  staticData: { access: { permission: qualityPermissions.lotView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/LotsPage"), "LotsPage"),
});

const qualityLotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/lots/$lotId",
  staticData: { access: { permission: qualityPermissions.lotView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/LotPage"), "LotPage"),
});

const qualityNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/notifications",
  staticData: { access: { permission: qualityPermissions.notificationView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/NotificationsPage"), "NotificationsPage"),
});

const qualityNotificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/notifications/$notificationId",
  staticData: { access: { permission: qualityPermissions.notificationView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/NotificationPage"), "NotificationPage"),
});

const qualitySamplingPointsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/sampling-points",
  staticData: { access: { permission: qualityPermissions.samplingPointView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/SamplingPointsPage"), "SamplingPointsPage"),
});

const qualityPoliciesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/policies",
  staticData: { access: { permission: qualityPermissions.policyView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/PoliciesPage"), "PoliciesPage"),
});

const qualityPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/policies/$policyId",
  staticData: { access: { permission: qualityPermissions.policyView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/PolicyPage"), "PolicyPage"),
});

const qualityReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/reports",
  staticData: { access: { permission: qualityPermissions.lotView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/ReportsPage"), "ReportsPage"),
});

const qualityPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/plans",
  staticData: { access: { permission: qualityPermissions.planView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/PlansPage"), "PlansPage"),
});

const qualityCodeSetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/code-sets",
  staticData: { access: { permission: qualityPermissions.planView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/CodeSetsPage"), "CodeSetsPage"),
});

const qualityTriggersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality/triggers",
  staticData: { access: { permission: qualityPermissions.triggerView, module: qualityModule } },
  component: lazyRouteComponent(() => import("../modules/quality/pages/TriggersPage"), "TriggersPage"),
});

const crmLeadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/leads",
  staticData: { access: { permission: crmPermissions.leadView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/LeadsPage"), "LeadsPage"),
});

const crmLeadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/leads/$leadId",
  staticData: { access: { permission: crmPermissions.leadView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/LeadPage"), "LeadPage"),
});

const crmAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/accounts",
  staticData: { access: { permission: crmPermissions.accountView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/AccountsPage"), "AccountsPage"),
});

const crmAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/accounts/$accountId",
  staticData: { access: { permission: crmPermissions.accountView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/AccountPage"), "AccountPage"),
});

const crmContactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/contacts",
  staticData: { access: { permission: crmPermissions.contactView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/ContactsPage"), "ContactsPage"),
});

const crmPipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/pipeline",
  staticData: { access: { permission: crmPermissions.opportunityView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/PipelinePage"), "PipelinePage"),
});

const crmOpportunitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/opportunities",
  staticData: { access: { permission: crmPermissions.opportunityView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/OpportunitiesPage"), "OpportunitiesPage"),
});

const crmOpportunityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/opportunities/$opportunityId",
  staticData: { access: { permission: crmPermissions.opportunityView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/OpportunityPage"), "OpportunityPage"),
});

const crmActivitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/activities",
  staticData: { access: { permission: crmPermissions.activityView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/ActivitiesPage"), "ActivitiesPage"),
});

const crmVisitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/visits",
  staticData: { access: { permission: crmPermissions.visitView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/VisitsPage"), "VisitsPage"),
});

const crmCheckInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/check-in",
  staticData: { access: { permission: crmPermissions.visitCreate, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/CheckInPage"), "CheckInPage"),
});

const crmCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/calendar",
  staticData: { access: { permission: crmPermissions.appointmentView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/CalendarPage"), "CalendarPage"),
});

const crmResourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/resources",
  staticData: { access: { permission: crmPermissions.resourceView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/ResourcesPage"), "ResourcesPage"),
});

const crmCollectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/collections",
  staticData: { access: { permission: crmPermissions.collectionView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/CollectionsPage"), "CollectionsPage"),
});

const crmCasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/cases",
  staticData: { access: { permission: crmPermissions.caseView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/CasesPage"), "CasesPage"),
});

const crmCaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/cases/$caseId",
  staticData: { access: { permission: crmPermissions.caseView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/CasePage"), "CasePage"),
});

const crmTargetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/targets",
  staticData: { access: { permission: crmPermissions.targetView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/TargetsPage"), "TargetsPage"),
});

const crmSegmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/segments",
  staticData: { access: { permission: crmPermissions.segmentView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/SegmentsPage"), "SegmentsPage"),
});

const crmReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/reports",
  staticData: { access: { permission: crmPermissions.reportView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/ReportsPage"), "ReportsPage"),
});

const crmPrivacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/privacy",
  staticData: { access: { permission: crmPermissions.privacyView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/PrivacyPage"), "PrivacyPage"),
});

const crmSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/crm/setup",
  staticData: { access: { permission: crmPermissions.setupView, module: crmModule } },
  component: lazyRouteComponent(() => import("../modules/crm/pages/SetupPage"), "SetupPage"),
});

const crmRoutes = [
  crmLeadsRoute,
  crmLeadRoute,
  crmAccountsRoute,
  crmAccountRoute,
  crmContactsRoute,
  crmPipelineRoute,
  crmOpportunitiesRoute,
  crmOpportunityRoute,
  crmActivitiesRoute,
  crmVisitsRoute,
  crmCheckInRoute,
  crmCalendarRoute,
  crmResourcesRoute,
  crmCollectionsRoute,
  crmCasesRoute,
  crmCaseRoute,
  crmTargetsRoute,
  crmSegmentsRoute,
  crmReportsRoute,
  crmPrivacyRoute,
  crmSetupRoute,
  procRequestsRoute,
  procRequestRoute,
  procSourcingRoute,
  procTendersRoute,
  procTenderRoute,
  procRefillsRoute,
  procRefillRoute,
  procSetupRoute,
  scmInternationalRoute,
  scmInternationalShipmentRoute,
  scmLocalRoute,
  scmLocalShipmentRoute,
  scmForecastsRoute,
  scmForecastRoute,
  scmPlansRoute,
  scmPlanRoute,
  scmAirwayBillsRoute,
  scmLoadingListsRoute,
  scmLoadingListRoute,
  scmLogisticsRoute,
  scmReferencesRoute,
  qualityLotsRoute,
  qualityLotRoute,
  qualityNotificationsRoute,
  qualityNotificationRoute,
  qualitySamplingPointsRoute,
  qualityPoliciesRoute,
  qualityPolicyRoute,
  qualityReportsRoute,
  qualityPlansRoute,
  qualityCodeSetsRoute,
  qualityTriggersRoute,
  leaseLeasesRoute,
  leaseLeaseRoute,
  leasePropertiesRoute,
  leaseUnitsRoute,
  leaseStraightLineRoute,
  leaseReportsRoute,
  jewelSalesRoute,
  jewelSaleRoute,
  jewelBuyBacksRoute,
  jewelTagsRoute,
  jewelAccountsRoute,
  jewelAccountRoute,
  jewelRefiningRoute,
  jewelWagesRoute,
  jewelReportsRoute,
  jewelPricesRoute,
  jewelItemsRoute,
  parkingEntryRoute,
  parkingExitRoute,
  parkingTicketsRoute,
  parkingShiftsRoute,
  parkingTariffsRoute,
  parkingLanesRoute,
  parkingCampaignsRoute,
  parkingReportsRoute,
  fleetVehiclesRoute,
  fleetVehicleRoute,
  fleetDriversRoute,
  fleetFuelRoute,
  fleetMaintenanceRoute,
  fleetAlertsRoute,
  fleetReportsRoute,
  fleetCarCardsRoute,
  fleetGarageOrdersRoute,
  fleetClearancesRoute,
];

const opsOffersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/offers",
  staticData: { access: { permission: opsPermissions.offerView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/OffersPage"), "OffersPage"),
});

const opsOfferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/offers/$offerId",
  staticData: { access: { permission: opsPermissions.offerView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/OfferPage"), "OfferPage"),
});

const opsContractsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/contracts",
  staticData: { access: { permission: opsPermissions.contractView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/ContractsPage"), "ContractsPage"),
});

const opsContractRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/contracts/$contractId",
  staticData: { access: { permission: opsPermissions.contractView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/ContractPage"), "ContractPage"),
});

const opsBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/billing",
  staticData: { access: { permission: opsPermissions.contractBill, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/BillingPage"), "BillingPage"),
});

const opsJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/jobs",
  staticData: { access: { permission: opsPermissions.jobView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/JobsPage"), "JobsPage"),
});

const opsJobRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/jobs/$jobId",
  staticData: { access: { permission: opsPermissions.jobView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/JobPage"), "JobPage"),
});

const opsProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/projects",
  staticData: { access: { permission: opsPermissions.projectView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/ProjectsPage"), "ProjectsPage"),
});

const opsRevenueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/revenue",
  staticData: { access: { permission: opsPermissions.revenueView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/RevenuePage"), "RevenuePage"),
});

const opsLinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/lines",
  staticData: { access: { permission: opsPermissions.lineView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/LinesPage"), "LinesPage"),
});

const opsKindsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/kinds",
  staticData: { access: { permission: opsPermissions.settingsView, module: opsModule } },
  component: lazyRouteComponent(() => import("../modules/ops/pages/KindsPage"), "KindsPage"),
});

const opsRoutes = [
  opsOffersRoute,
  opsOfferRoute,
  opsContractsRoute,
  opsContractRoute,
  opsBillingRoute,
  opsJobsRoute,
  opsJobRoute,
  opsProjectsRoute,
  opsRevenueRoute,
  opsLinesRoute,
  opsKindsRoute,
];

const workRoutes = [
  workTasksRoute,
  workTaskRoute,
  workBoardRoute,
  workCalendarRoute,
  workQueuesRoute,
  workFormsRoute,
  workFormRoute,
  workChecklistsRoute,
];

const msgRoutes = [
  msgMonitorRoute,
  msgSegmentsRoute,
  msgConsentRoute,
  msgSurveysRoute,
  msgTriggersRoute,
  msgTemplatesRoute,
  msgAccountsRoute,
];

const assetsRoutes = [
  faRegisterRoute,
  faAssetRoute,
  faClassesRoute,
  faAcquisitionListRoute,
  faAcquisitionRoute,
  faSettlementListRoute,
  faSettlementRoute,
  faTransferListRoute,
  faTransferRoute,
  faSaleListRoute,
  faSaleRoute,
  faScrapListRoute,
  faScrapRoute,
  faImpairmentListRoute,
  faImpairmentRoute,
  faRevaluationListRoute,
  faRevaluationRoute,
  faDepreciationRoute,
  faLeasesRoute,
  faLeaseRoute,
  faMaintenanceRoute,
  faCountsRoute,
  faCountRoute,
  faReportRegisterRoute,
  faReportRollForwardRoute,
  faReportForecastRoute,
  faReportLeaseMaturityRoute,
];

const taxRoutes = [
  taxReturnsRoute,
  taxReturnRoute,
  taxEInvoicesRoute,
  taxCodesRoute,
  taxDeterminationRoute,
  taxSettingsRoute,
  taxLedgerRoute,
  taxReconciliationRoute,
  taxWithholdingRoute,
];

/* Financial reports (RP1-RP7, plan module `gl`): one route per report, each behind its own view key (RP6). */
const rptBalanceSheetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/balance-sheet",
  staticData: { access: { permission: rpt.balanceSheetView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/ReportRoutes"), "BalanceSheetReport"),
});

const rptIncomeStatementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/income-statement",
  staticData: { access: { permission: rpt.incomeView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/ReportRoutes"), "IncomeStatementReport"),
});

const rptComprehensiveIncomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/comprehensive-income",
  staticData: { access: { permission: rpt.incomeView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/ReportRoutes"), "ComprehensiveIncomeReport"),
});

const rptCashFlowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/cash-flow",
  staticData: { access: { permission: rpt.cashFlowView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/CashFlowPage"), "CashFlowPage"),
});

const rptRatiosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/ratios",
  staticData: { access: { permission: rpt.ratiosView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/RatiosPage"), "RatiosPage"),
});

const rptVersionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/statement-versions",
  staticData: { access: { permission: rpt.versionView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/VersionsPage"), "VersionsPage"),
});

const rptVersionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/statement-versions/$versionId",
  staticData: { access: { permission: rpt.versionView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/VersionDesignerPage"), "VersionDesignerPage"),
});

const rptSchedulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/schedules",
  staticData: { access: { permission: rpt.scheduleView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/SchedulesPage"), "SchedulesPage"),
});

const rptArchiveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/issued-documents",
  staticData: { access: { permission: rpt.archiveView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/ArchivePage"), "ArchivePage"),
});

const rptFeedTokensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/feed-tokens",
  staticData: { access: { permission: rpt.feedView, module: reportsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/FeedTokensPage"), "FeedTokensPage"),
});

/* Reports part B (RP8-RP11): dashboards (plan module `analytics`); query reports, footfall and franchise exports (core). */
const rptDashboardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/dashboards",
  staticData: { access: { permission: rptb.dashboardView, module: analyticsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/DashboardsPage"), "DashboardsPage"),
});

const rptQueryReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/query-reports",
  staticData: { access: { permission: rptb.queryReportView } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/QueryReportsPage"), "QueryReportsPage"),
});

const rptFootfallRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/footfall",
  staticData: { access: { permission: rptb.footfallView } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/FootfallPage"), "FootfallPage"),
});

const rptFranchiseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/franchise-exports",
  staticData: { access: { permission: rptb.franchiseView } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/FranchiseExportsPage"), "FranchiseExportsPage"),
});

/* AI settings (AI1-AI6) and OpenRouter's callback (one-click connection, AI5). */
const rptAiSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/ai-settings",
  staticData: { access: { permission: rptb.aiSettingsView, module: analyticsModule } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/AiSettingsPage"), "AiSettingsPage"),
});

const rptOpenRouterCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/ai-settings/openrouter",
  staticData: { access: { permission: rptb.aiSettingsUpdate, module: analyticsModule } },
  component: lazyRouteComponent(
    () => import("../modules/reports/pages/OpenRouterCallbackPage"),
    "OpenRouterCallbackPage",
  ),
});

/* The report inbox: every signed-in user has one (deliveries addressed to them only). */
const rptInboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports/inbox",
  staticData: { access: { public: true } },
  component: lazyRouteComponent(() => import("../modules/reports/pages/InboxPage"), "InboxPage"),
});

/* Loyalty & Gift Cards (docs/contracts/loyalty.md). */
const lyAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/accounts",
  staticData: { access: { permission: ly.accountView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/AccountsPage"), "AccountsPage"),
});

const lyAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/accounts/$accountId",
  staticData: { access: { permission: ly.accountView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/routePages"), "AccountObject"),
});

const lyGiftCardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/gift-cards",
  staticData: { access: { permission: ly.giftCardView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/GiftCardsPage"), "GiftCardsPage"),
});

const lyProgrammesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/programmes",
  staticData: { access: { permission: ly.programmeView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/ProgrammesPage"), "ProgrammesPage"),
});

const lyDiscountCardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/discount-cards",
  staticData: { access: { permission: ly.discountCardView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/routePages"), "DiscountCardList"),
});

const lyGroupCardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/group-discount-cards",
  staticData: { access: { permission: ly.groupCardView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/routePages"), "GroupCardList"),
});

const lyAdjustmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/adjustments",
  staticData: { access: { permission: ly.accountView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/AdjustmentsPage"), "AdjustmentsPage"),
});

const lyReconciliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/reconciliation",
  staticData: { access: { permission: ly.reconciliationView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/ReconciliationPage"), "ReconciliationPage"),
});

const lyRatingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/ratings",
  staticData: { access: { permission: ly.ratingView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/RatingsPage"), "RatingsPage"),
});

const lySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/loyalty/settings",
  staticData: { access: { permission: ly.settingsView, module: loyaltyModule } },
  component: lazyRouteComponent(() => import("../modules/loyalty/pages/SettingsPage"), "SettingsPage"),
});

const loyaltyRoutes = [
  lyAccountsRoute,
  lyAccountRoute,
  lyGiftCardsRoute,
  lyProgrammesRoute,
  lyDiscountCardsRoute,
  lyGroupCardsRoute,
  lyAdjustmentsRoute,
  lyReconciliationRoute,
  lyRatingsRoute,
  lySettingsRoute,
];

const reportsRoutes = [
  rptBalanceSheetRoute,
  rptIncomeStatementRoute,
  rptComprehensiveIncomeRoute,
  rptCashFlowRoute,
  rptRatiosRoute,
  rptVersionsRoute,
  rptVersionRoute,
  rptSchedulesRoute,
  rptArchiveRoute,
  rptFeedTokensRoute,
  rptAiSettingsRoute,
  rptOpenRouterCallbackRoute,
  rptInboxRoute,
  rptDashboardsRoute,
  rptQueryReportsRoute,
  rptFootfallRoute,
  rptFranchiseRoute,
];

const routes = [
  homeRoute,
  systemStatusRoute,
  profileRoute,
  usersRoute,
  userRoute,
  rolesRoute,
  roleRoute,
  auditRoute,
  settingsGroupRoute,
  deviceProfilesRoute,
  deviceAgentsRoute,
  devicePairingRoute,
  deviceJobsRoute,
  deviceReleasesRoute,
  deviceScansRoute,
  posProfilesRoute,
  posTerminalsRoute,
  posShiftsRoute,
  posIssuesRoute,
  posDrawerRoute,
  posCashAuditRoute,
  posSalesRoute,
  posKitchenTimesRoute,
  posCardsRoute,
  posBranchOperationsRoute,
  posOverridesRoute,
  exportsRoute,
  platformSettingsRoute,
  approvalsRoute,
  approvalTemplatesRoute,
  numberSeriesRoute,
  accountsRoute,
  accountGroupsRoute,
  costCentersRoute,
  projectsRoute,
  currenciesRoute,
  partiesRoute,
  partyRoute,
  partySettingsRoute,
  companiesRoute,
  companyRoute,
  itemsRoute,
  itemRoute,
  itemQuickUpdateRoute,
  barcodeLabelsRoute,
  lookupsRoute,
  itemCategoriesRoute,
  unitGroupsRoute,
  warehousesRoute,
  accountFamiliesRoute,
  modifierSetsRoute,
  priceListsRoute,
  serviceTariffsRoute,
  priceListRoute,
  serviceTariffRoute,
  journalVouchersRoute,
  journalVoucherRoute,
  journalEntriesRoute,
  journalEntryRoute,
  recurringJournalsRoute,
  recurringRunsRoute,
  recurringJournalRoute,
  fiscalYearsRoute,
  revaluationRoute,
  ledgerSettingsRoute,
  trialBalanceRoute,
  statementRoute,
  financialStatementsRoute,
  costCentreReportRoute,
  projectReportRoute,
  deletedVouchersRoute,
  invOpeningStockListRoute,
  invOpeningStockRoute,
  invGoodsIssueListRoute,
  invGoodsIssueRoute,
  invGoodsReceiptListRoute,
  invGoodsReceiptRoute,
  invAdjustmentListRoute,
  invAdjustmentRoute,
  invTransferListRoute,
  invTransferRoute,
  invStockTypeChangeListRoute,
  invStockTypeChangeRoute,
  invRevaluationListRoute,
  invRevaluationRoute,
  invAwaitingRoute,
  invAwaitingObjectRoute,
  invCountsRoute,
  invCountRoute,
  invLandedCostsRoute,
  invLandedCostRoute,
  invReplenishmentRoute,
  invReorderRulesRoute,
  invCostingRoute,
  invReasonsRoute,
  invWarehouseAccessRoute,
  invAccountSettingsRoute,
  invReportStockCardRoute,
  invReportStockBalanceRoute,
  invReportValuationRoute,
  invReportMovementsRoute,
  invReportBatchAgeingRoute,
  invReportSerialTraceRoute,
  invReportSlowMovingRoute,
  invReportReconciliationRoute,
  purchRequisitionListRoute,
  purchRequisitionRoute,
  purchRfqListRoute,
  purchRfqRoute,
  purchQuotationListRoute,
  purchQuotationRoute,
  purchOrderListRoute,
  purchOrderRoute,
  purchContractListRoute,
  purchContractRoute,
  purchGoodsReceiptListRoute,
  purchGoodsReceiptRoute,
  purchInvoiceListRoute,
  purchInvoiceRoute,
  purchReturnRequestListRoute,
  purchReturnRequestRoute,
  purchReturnListRoute,
  purchReturnRoute,
  purchDebitNoteListRoute,
  purchDebitNoteRoute,
  purchGrIrRoute,
  purchReturnReasonsRoute,
  purchReportPayablesAgingRoute,
  purchReportSupplierStatementRoute,
  purchReportOpenOrdersRoute,
  purchReportPurchaseAnalysisRoute,
  purchReportMatchExceptionsRoute,
  purchReportPriceHistoryRoute,
  tenantsRoute,
  tenantRoute,
  ...cashRoutes,
  ...salesRoutes,
  ...taxRoutes,
  ...hrRoutes,
  ...assetsRoutes,
  ...msgRoutes,
  ...workRoutes,
  ...crmRoutes,
  ...opsRoutes,
  ...reportsRoutes,
  ...loyaltyRoutes,
];
const routeTree = rootRoute.addChildren(routes);

export interface AppRouterOptions {
  /**
   * Extra routes and navigation entries. Only the dev-only samples use this, and only when
   * `import.meta.env.DEV` is true — production builds contain neither the routes nor their mock data.
   */
  extra?: { routes: (root: typeof rootRoute) => AnyRoute[]; navigation: readonly NavigationEntry[] };
  mode: HostMode;
  navigation: readonly NavigationEntry[];
}

export function createAppRouter({ extra, mode, navigation }: AppRouterOptions) {
  // Dev routes are not part of the typed tree on purpose: production code can never link to them.
  const tree = extra
    ? (rootRoute.addChildren([...routes, ...extra.routes(rootRoute)]) as unknown as typeof routeTree)
    : routeTree;
  return createRouter({
    routeTree: tree,
    defaultPreload: "intent",
    defaultPendingComponent: RoutePending,
    defaultPendingMs: 300,
    defaultErrorComponent: RouteError,
    context: {
      navigation: extra ? [...navigation, ...extra.navigation] : navigation,
      access: noAccess,
      mode,
    },
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
